import React, { useEffect, useState } from "react";

import {
  getLocalStorageFieldFilterOptions,
  setLocalStorageFieldFilterOptions,
  useGetFarms,
  useGetFieldPracticeFilterOptions,
  useGetPracticeCrops,
  useGetPracticeFilteredFieldIds,
  useTranslation,
} from "utils";

import { FieldListField, ItemsWithMissingDataByFieldId } from "models";

import { Button, CheckboxSegmented, Flex, Icon, Modal, Tooltip } from "components";

import { hasItemsWithMissingData } from "pages/app/all-field-view/utils/AllFieldsUtils";

export enum DATA_STATUS {
  ALL = "all",
  MISSING = "missing",
  COMPLETED = "completed",
}

export type FieldFilterOptionsData = {
  farmId: string;
  cropIds: string[];
  years: number[];
  dataStatus: DATA_STATUS;
};

export const FieldFilterOptions = ({
  fields,
  filteredFieldIds,
  setFilteredFieldIds,
  selectedFilterOptionsCount,
  setSelectedFilterOptionsCount,
  appliedCropIds,
  appliedYears,
  appliedDataStatus,
  setAppliedCropIds,
  setAppliedYears,
  setAppliedDataStatus,
  farmId,
  handleClearFilters,
  itemsWithMissingDataByFieldId,
}: {
  fields: FieldListField[];
  filteredFieldIds: string[] | null;
  setFilteredFieldIds: (fieldIds: string[] | null) => void;
  selectedFilterOptionsCount: number;
  setSelectedFilterOptionsCount: (count: number) => void;
  appliedCropIds: string[];
  appliedYears: number[];
  appliedDataStatus: DATA_STATUS;
  setAppliedCropIds: (cropIds: string[]) => void;
  setAppliedYears: (years: number[]) => void;
  setAppliedDataStatus: (dataStatus: DATA_STATUS) => void;
  farmId: string;
  handleClearFilters: () => void;
  itemsWithMissingDataByFieldId: ItemsWithMissingDataByFieldId;
}) => {
  const { t } = useTranslation("allFieldView");

  const { currentFarm } = useGetFarms();
  const { data: practiceCrops } = useGetPracticeCrops({});
  const {
    data: fieldPracticeFilterOptions,
    isFetching: isFetchingFieldPracticeFilterOptions,
    isPending: isPendingFieldPracticeFilterOptions,
  } = useGetFieldPracticeFilterOptions({ farmId: currentFarm?.id });
  const { mutateAsync: getFilteredFieldIds } = useGetPracticeFilteredFieldIds();

  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showAllCrops, setShowAllCrops] = useState(false);

  // Internal state (what user is editing in the modal but is not valid until the apply button is clicked)
  const [internalCropIds, setInternalCropIds] = useState<string[]>([]);
  const [internalYears, setInternalYears] = useState<number[]>([]);
  const [internalDataStatus, setInternalDataStatus] = useState<DATA_STATUS>(DATA_STATUS.ALL);
  const [isLoading, setIsLoading] = useState(false);

  const cropIdsToDisplay = !fieldPracticeFilterOptions?.cropIds
    ? []
    : showAllCrops
    ? fieldPracticeFilterOptions.cropIds
    : fieldPracticeFilterOptions.cropIds.slice(0, 9);

  const appliedCropNames = appliedCropIds.map((cropId) => practiceCrops?.find((crop) => crop.id === cropId)?.name);
  const filterOptionsCountTooltipContent = `${[...appliedCropNames, ...appliedYears, appliedDataStatus].join(", ")} ${t(
    "fieldFilterOptions.modal.sections.dataStatus.title"
  ).toLowerCase()}`;

  const getSelectedFilterOptionsCount = (cropIds: string[], years: number[], dataStatus: DATA_STATUS) =>
    cropIds.length + years.length + (dataStatus === DATA_STATUS.ALL ? 0 : 1);

  // Sync internal state with applied state when modal opens
  const handleModalOpen = () => {
    if (fields.length === 0) return;

    setInternalCropIds(appliedCropIds);
    setInternalYears(appliedYears);
    setInternalDataStatus(appliedDataStatus as DATA_STATUS);
    setShowFiltersModal(true);
  };

  const resetInternalFilters = () => {
    setInternalCropIds([]);
    setInternalYears([]);
    setInternalDataStatus(DATA_STATUS.ALL);
  };

  const handleModalCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isLoading) return;

    const target = e.target as HTMLElement;
    const button = target.closest("button");

    // if the clicked target is the modal close button, close the modal and don't apply changes
    if (button?.classList.contains("ant-modal-close")) {
      setShowFiltersModal(false);
    } else {
      // reset internal state to empty
      resetInternalFilters();
    }
  };

  const handleApplyFilters = async (cropIds: string[], years: number[], dataStatus: DATA_STATUS) => {
    if (isLoading) return;

    setIsLoading(true);

    let output: string[] | null = [];
    const selectedOptionsCount = getSelectedFilterOptionsCount(cropIds, years, dataStatus);

    try {
      // if no filters are applied, set filteredFieldIds to null
      if (cropIds.length === 0 && years.length === 0 && dataStatus === DATA_STATUS.ALL) {
        output = null;
      } else {
        const filteredFieldIdsByPractice = await getFilteredFieldIds({
          cropIds: cropIds,
          years: years,
        });

        if (dataStatus === DATA_STATUS.ALL) {
          output = filteredFieldIdsByPractice.fieldIds;
        } else {
          const filteredFieldIdsByDataStatus = filteredFieldIdsByPractice.fieldIds.filter((fieldId) => {
            const itemsWithMissingData = itemsWithMissingDataByFieldId[fieldId];
            // in case the field is not in the list, it means it's not enrolled and we don't want to show it
            if (!itemsWithMissingData) return false;
            const fieldHasMissingData = hasItemsWithMissingData(itemsWithMissingData);

            return dataStatus === DATA_STATUS.COMPLETED ? !fieldHasMissingData : fieldHasMissingData;
          });

          output = filteredFieldIdsByDataStatus;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setAppliedCropIds(cropIds);
      setAppliedYears(years);
      setAppliedDataStatus(dataStatus);
      setSelectedFilterOptionsCount(selectedOptionsCount);
      setFilteredFieldIds(output);
      setLocalStorageFieldFilterOptions({
        farmId,
        cropIds: cropIds,
        years: years,
        dataStatus: dataStatus,
      });

      setIsLoading(false);
      setShowFiltersModal(false);
    }
  };

  // load saved filter options when farm changes or component mounts
  useEffect(() => {
    if (!currentFarm?.id || isPendingFieldPracticeFilterOptions || isFetchingFieldPracticeFilterOptions || isLoading) return;

    const savedFilterOptions = getLocalStorageFieldFilterOptions();

    if (!savedFilterOptions || savedFilterOptions?.farmId !== currentFarm.id) {
      handleClearFilters();
      return;
    }

    const { cropIds, years, dataStatus } = savedFilterOptions;
    // we need to make sure that the saved options are valid for the current farm (some of the selected options may have been removed from the farm in the meanWhile)
    const validCropIds = cropIds.filter((cropId) => fieldPracticeFilterOptions?.cropIds.some((filterOptionCropId) => filterOptionCropId === cropId));
    const validYears = years.filter((year) => fieldPracticeFilterOptions?.years.some((filterOptionYear) => filterOptionYear === year));
    const validDataStatus = Object.values(DATA_STATUS).includes(dataStatus) ? dataStatus : DATA_STATUS.ALL;

    setInternalCropIds(validCropIds);
    setInternalYears(validYears);
    setInternalDataStatus(validDataStatus);
    const filterCount = getSelectedFilterOptionsCount(validCropIds, validYears, validDataStatus);
    setSelectedFilterOptionsCount(filterCount);

    if (
      filteredFieldIds === null &&
      (savedFilterOptions.cropIds.length > 0 || savedFilterOptions.years.length > 0 || savedFilterOptions.dataStatus !== DATA_STATUS.ALL)
    ) {
      handleApplyFilters(savedFilterOptions.cropIds, savedFilterOptions.years, savedFilterOptions.dataStatus as DATA_STATUS);
    }
  }, [currentFarm?.id, filteredFieldIds, fieldPracticeFilterOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // validate and clean up applied filters when fieldPracticeFilterOptions changes
  useEffect(() => {
    if (!fieldPracticeFilterOptions || isLoading || isPendingFieldPracticeFilterOptions || isFetchingFieldPracticeFilterOptions) return;

    const validAppliedCropIds = appliedCropIds.filter((cropId) => fieldPracticeFilterOptions.cropIds.includes(cropId));
    const validAppliedYears = appliedYears.filter((year) => fieldPracticeFilterOptions.years.includes(year));

    // if any filters were removed, update the applied state and localStorage
    if (validAppliedCropIds.length !== appliedCropIds.length || validAppliedYears.length !== appliedYears.length) {
      const updatedCropIds = validAppliedCropIds;
      const updatedYears = validAppliedYears;
      const filterCount = getSelectedFilterOptionsCount(updatedCropIds, updatedYears, appliedDataStatus);

      setAppliedCropIds(updatedCropIds);
      setAppliedYears(updatedYears);
      setSelectedFilterOptionsCount(filterCount);
      setLocalStorageFieldFilterOptions({
        farmId,
        cropIds: updatedCropIds,
        years: updatedYears,
        dataStatus: appliedDataStatus,
      });

      handleApplyFilters(updatedCropIds, updatedYears, appliedDataStatus);
    }
  }, [fieldPracticeFilterOptions, appliedCropIds, appliedYears, appliedDataStatus, farmId, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="field-filter-options-wrapper" data-testid="field-filter-options-wrapper">
      <Flex className={`field-filter-options-button ${showFiltersModal ? "active" : ""} ${fields.length === 0 ? "disabled" : ""}`}>
        <Flex
          className={`field-filter-options-container ${selectedFilterOptionsCount > 0 ? "with-delete-option" : ""}`}
          align="center"
          gap="0.5rem"
          onClick={handleModalOpen}
          role="button"
          tabIndex={0}
          aria-label={t("fieldFilterOptions.mainButtonContent")}
          data-testid="field-filter-options-button"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleModalOpen();
            }
          }}
        >
          <p className="paragraph-m-bold margin-0">{t("fieldFilterOptions.mainButtonContent")}</p>
          {selectedFilterOptionsCount > 0 && (
            <Tooltip placement="top" title={filterOptionsCountTooltipContent}>
              <Flex className="selected-filter-options-count" align="center" justify="center" data-testid="filter-count-badge">
                <p className="paragraph-s-bold margin-0">{selectedFilterOptionsCount}</p>
              </Flex>
            </Tooltip>
          )}
        </Flex>
        <Flex
          className={`field-filter-options-close-icon-container ${selectedFilterOptionsCount > 0 ? "visible" : ""}`}
          align="center"
          justify="center"
          onClick={handleClearFilters}
          role="button"
          tabIndex={0}
          aria-label={t("fieldFilterOptions.modal.clearAll")}
          data-testid="field-filter-clear-button"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClearFilters();
            }
          }}
        >
          <Icon iconName="tabler/x" size="s" />
        </Flex>
      </Flex>
      <Modal
        cancelText={t("fieldFilterOptions.modal.clearAll")}
        className="field-filter-options-modal"
        destroyOnHidden
        loading={isPendingFieldPracticeFilterOptions || isFetchingFieldPracticeFilterOptions || !currentFarm || isLoading}
        okText={t("fieldFilterOptions.modal.apply")}
        onCancel={handleModalCancel}
        onOk={() => handleApplyFilters(internalCropIds, internalYears, internalDataStatus)}
        showModal={showFiltersModal}
        title={t("fieldFilterOptions.modal.title")}
        data-testid="field-filter-modal"
        content={
          <>
            {fieldPracticeFilterOptions?.cropIds && fieldPracticeFilterOptions?.cropIds?.length > 0 && (
              <>
                <Flex vertical gap="1rem" data-testid="crops-section">
                  <h6>{t("fieldFilterOptions.modal.sections.crops.title")}</h6>
                  <Flex vertical gap="0.5rem">
                    <Flex className="crop-checkboxes-container" wrap="wrap" gap="0.5rem" data-testid="crop-checkboxes-container">
                      {cropIdsToDisplay.map((cropId) => (
                        <CheckboxSegmented
                          key={cropId}
                          label={practiceCrops?.find((crop) => crop.id === cropId)?.name}
                          checked={internalCropIds.includes(cropId)}
                          data-testid={`crop-checkbox-${cropId}`}
                          onChange={() =>
                            setInternalCropIds(internalCropIds.includes(cropId) ? internalCropIds.filter((id) => id !== cropId) : [...internalCropIds, cropId])
                          }
                        />
                      ))}
                    </Flex>
                    {fieldPracticeFilterOptions.cropIds.length > 9 && (
                      <Button type="text" onClick={() => setShowAllCrops(!showAllCrops)} data-testid="show-all-crops-button">
                        {showAllCrops ? t("fieldFilterOptions.modal.showLess") : t("fieldFilterOptions.modal.showAll")}
                      </Button>
                    )}
                  </Flex>
                </Flex>
                <span className="dotted-line-divider" />
              </>
            )}
            {fieldPracticeFilterOptions?.years && fieldPracticeFilterOptions?.years?.length > 0 && (
              <>
                <Flex vertical gap="1rem" data-testid="years-section">
                  <h6>{t("fieldFilterOptions.modal.sections.harvestYear.title")}</h6>
                  <Flex wrap="wrap" gap="0.5rem" data-testid="year-checkboxes-container">
                    {fieldPracticeFilterOptions.years.map((year) => (
                      <CheckboxSegmented
                        key={year}
                        label={year.toString()}
                        checked={internalYears.includes(year)}
                        data-testid={`year-checkbox-${year}`}
                        onChange={() => setInternalYears(internalYears.includes(year) ? internalYears.filter((y) => y !== year) : [...internalYears, year])}
                      />
                    ))}
                  </Flex>
                </Flex>
                <span className="dotted-line-divider" />
              </>
            )}
            <Flex vertical gap="1rem" data-testid="data-status-section">
              <h6>{t("fieldFilterOptions.modal.sections.dataStatus.title")}</h6>
              <Flex wrap="wrap" gap="0.5rem" data-testid="data-status-checkboxes-container">
                {Object.values(DATA_STATUS).map((status) => (
                  <CheckboxSegmented
                    key={status}
                    label={t(`fieldFilterOptions.modal.sections.dataStatus.options.${status}`)}
                    checked={internalDataStatus === status}
                    data-testid={`data-status-checkbox-${status}`}
                    onChange={() => setInternalDataStatus(status)}
                  />
                ))}
              </Flex>
            </Flex>
          </>
        }
      />
    </div>
  );
};
